import type { StorePageEditResult } from "../store-page/types";
import { storePageApi } from "../store-page/client";
import { useStorePageDraft } from "./use-store-page-draft";
import { useStorePageOperation } from "./use-store-page-operation";
import {
  saveDraftOperation,
  publishDraftOperation,
  changeRevisionOperation,
} from "./store-page.mutations";

export function useStorePageEditor({
  slug,
  enabled,
  refreshPublished,
}: {
  slug: string;
  enabled: boolean;
  refreshPublished: () => Promise<void>;
}) {
  const draft = useStorePageDraft({ slug, enabled });
  const operation = useStorePageOperation({
    enabled,
    onConflict: draft.applyConflict,
  });
  const { runOperation } = operation;
  const failure = operation.failure ?? draft.readFailure;
  const canWrite =
    enabled &&
    draft.loaded &&
    draft.metadataReady &&
    !operation.activeOperation &&
    !failure &&
    !draft.conflict;
  const canSave = canWrite && draft.fieldErrors.length === 0;
  const canPublish =
    canSave &&
    draft.status !== "pending_review" &&
    (draft.baseRevisionId !== null || draft.dirty);
  const refreshAfterWrite = async () => {
    await draft.refreshAfterWrite();
    await refreshPublished();
  };
  async function save({ note = null }: { readonly note?: string | null } = {}) {
    if (!canSave) return null;

    return runOperation(saveDraftOperation({ slug, draft, note }));
  }
  async function publish() {
    if (!canPublish) return;

    await runOperation(
      publishDraftOperation({ slug, draft, refresh: refreshAfterWrite }),
    );
  }
  async function changeRevision({
    operation: action,
    change,
  }: {
    readonly operation: "approve" | "reject" | "revert";
    readonly change: (key: string) => Promise<StorePageEditResult>;
  }) {
    if (!canWrite) return null;

    return runOperation(
      changeRevisionOperation({
        operation: action,
        draft,
        change,
        refresh: refreshAfterWrite,
      }),
    );
  }
  return {
    loaded: draft.loaded,
    loading: draft.loading,
    mutating: operation.activeOperation !== null,
    failure,
    retry: async () => {
      if (operation.failure) await operation.retry();
      else await draft.refresh();
    },
    discardChanges: () => {
      draft.discardChanges();
      operation.clearFailure();
    },
    content: draft.content,
    updateContent: (patch: Parameters<typeof draft.updateContent>[0]) => {
      operation.clearInvalidWrite();
      draft.updateContent(patch);
    },
    dirty: draft.dirty,
    lintWarnings: draft.lintWarnings,
    fieldErrors: draft.fieldErrors,
    canWrite,
    canSave,
    canPublish,
    baseRevisionId: draft.baseRevisionId,
    currentRevisionId: draft.currentRevisionId,
    status: draft.status,
    firstPublishedAt: draft.firstPublishedAt,
    revisions: draft.revisions,
    conflict: draft.conflict,
    saving: operation.activeOperation === "save",
    publishing: operation.activeOperation === "publish",
    lastAction: draft.lastAction,
    save,
    publish,
    approve: (revisionId: string) =>
      changeRevision({
        operation: "approve",
        change: (key) =>
          storePageApi.approve({ slug, revisionId, idempotencyKey: key }),
      }),
    reject: ({ revisionId, reason }: { revisionId: string; reason: string }) =>
      changeRevision({
        operation: "reject",
        change: (key) =>
          storePageApi.reject({
            slug,
            revisionId,
            reason,
            idempotencyKey: key,
          }),
      }),
    revert: (revisionId: string) =>
      changeRevision({
        operation: "revert",
        change: (key) =>
          storePageApi.revert({ slug, revisionId, idempotencyKey: key }),
      }),
    resolveConflictKeepMine: draft.resolveConflictKeepMine,
    resolveConflictTakeTheirs: draft.resolveConflictTakeTheirs,
    refresh: async () => {
      if (!operation.failure) await draft.refresh();
    },
  };
}
